import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../../../constants';
import { EventCardComponent } from '../../../schedule-calendar-components/event-card';
import { OverlappingEventIndicatorVariantType } from '../../calendar-view-v3/overlapping-event-indicator';
import { getDurationFromHours } from '../../utils';
import { GridEventCard } from '../atoms/grid-event-card';

type GridScheduleRequestCardProps = {
  id: string;
  title: string;
  startDate: string;
  startHour: string;
  endHour: string;
  locationName?: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  personStatus?: string;
  eventTypeColor?: string;
  backgroundColor?: string;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  scheduleRequestEventData?: ScheduleRequestEventData;
};

//#region Card

export const GridScheduleRequestCard = ({
  id,
  title,
  startDate,
  startHour,
  endHour,
  locationName = '',
  locationId,
  entityId,
  entityName,
  personStatus,
  eventTypeColor,
  backgroundColor = theme.colors.white,
  overlappingIndicatorVariant,
  isYesterdayEvent,
  scheduleRequestEventData,
}: GridScheduleRequestCardProps) => {
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);
  const primaryColor = eventTypeColor ?? PRIMARY_EVENT_COLOR;

  return (
    <GridEventCard id={id} primaryColor={primaryColor} backgroundColor={backgroundColor} isRequest>
      <GridEventCard.ShellWrapper>
        <GridEventCard.Shell>
          <GridEventCard.PersonStatusBar personStatus={personStatus} />
          <GridEventCard.EventColoredLine />
          <EventCardComponent
            id={id}
            eventType='request'
            title={title}
            duration={duration}
            locationName={locationName}
            locationId={locationId}
            providerId={entityId}
            providerName={entityName}
            isExpanded={false}
            startDate={startDate}
            overlappingIndicatorVariant={overlappingIndicatorVariant}
            isYesterdayEvent={isYesterdayEvent}
            scheduleRequestEventData={scheduleRequestEventData}
          />
        </GridEventCard.Shell>
      </GridEventCard.ShellWrapper>
    </GridEventCard>
  );
};

//#endregion

//#region Tooltip

type GridScheduleRequestTooltipProps = {
  startHour: string;
  endHour: string;
  scheduleRequestEventData?: ScheduleRequestEventData;
  appointmentType?: string;
};

export const GridScheduleRequestTooltip = ({
  startHour,
  endHour,
  scheduleRequestEventData,
  appointmentType,
}: GridScheduleRequestTooltipProps) => {
  const { t } = useTranslation('schedule');
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);

  const allRequestedOpenings = scheduleRequestEventData?.allRequestedOpenings;
  // startDate is YYYY-MM-DD and startTime is HH:mm:ss, so string compare sorts chronologically
  const sortedRequestedOpenings = useMemo(
    () =>
      [...(allRequestedOpenings ?? [])].sort((a, b) =>
        `${a.startDate} ${a.startTime}`.localeCompare(`${b.startDate} ${b.startTime}`)
      ),
    [allRequestedOpenings]
  );
  const totalRequestedOpenings = sortedRequestedOpenings.length;
  const isMultipleOpenings = totalRequestedOpenings > 1;

  const name = isMultipleOpenings ? t('All Requested Times') : t('Requested Appointment');
  const personName = getFullName(scheduleRequestEventData?.schedulee ?? {});
  const resolvedAppointmentType = scheduleRequestEventData?.appointmentTypeDetails?.displayName || appointmentType;

  return (
    <div css={scheduleRequestTooltipStyle}>
      <Truncated as={Text} weight='bold' size='medium' color='white'>
        {name}
      </Truncated>
      {!!personName && (
        <Truncated as={Text} size='medium' color='white'>
          {personName}
        </Truncated>
      )}
      {!!resolvedAppointmentType && (
        <Truncated as={Text} size='medium' color='white'>
          {resolvedAppointmentType}
        </Truncated>
      )}
      {isMultipleOpenings ? (
        sortedRequestedOpenings.map((opening, index) => {
          const openingDuration = getDurationFromHours(opening.startTime, opening.endTime);
          const dayString = dayjs(opening.startDate).format('MMM D');
          return (
            <Truncated key={`${opening.startDate}-${opening.startTime}-${index}`} as={Text} size='small' color='white'>
              {`${dayString} ${openingDuration}`}
            </Truncated>
          );
        })
      ) : (
        <Truncated as={Text} size='small' color='white'>
          {duration}
        </Truncated>
      )}
    </div>
  );
};

const scheduleRequestTooltipStyle = {
  display: 'grid',
  gap: theme.spacing(0.25),
  maxWidth: '200px',
};

//#endregion
